import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ConversacionResumen, FuentePatroclo, PasoPatroclo } from '@manejo-alcanza/contracts';
import {
  borrarConversacionPatroclo,
  conversacionesPatroclo,
  conversacionPatroclo,
  ErrorApi,
  preguntarAPatroclo,
} from '../api';
import { Aviso, Boton, Error_ } from '../componentes/basicos';
import { MensajeAsistente } from '../componentes/patroclo/MensajeAsistente';
import { LogoPatroclo } from '../componentes/patroclo/LogoPatroclo';
import { NavPatroclo } from '../componentes/patroclo/NavPatroclo';
import { pausaDe, type Pausa } from '../componentes/patroclo/pausa';

/**
 * La pestaña de Patroclo, el asistente de IA (E8-01): el chat.
 *
 * Portado de `paginas/Chat.tsx` de Patroclo-Local (traspaso §6). Lo que cambió:
 * habla con `/api/patroclo/*` con la sesión y el CSRF de siempre —sin token,
 * sin URL propia—, usa los componentes y los colores de ManejoAlcanza, y los
 * avisos son los del proxy: «espera un momento» (429), «fuera de servicio por
 * hoy» (503 con `Retry-After`) y «no está disponible» (503 sin él).
 *
 * Que la pestaña solo la vean los roles habilitados es comodidad: la
 * autorización de verdad está en el proxy y en Patroclo.
 */

interface MensajeVista {
  clave: string;
  rol: 'usuario' | 'asistente' | 'error';
  contenido: string;
  fuentes?: FuentePatroclo[];
  pasos?: PasoPatroclo[];
  rechazada?: boolean;
  tiempoMs?: number | null;
  idEjecucion?: string;
}

const SUGERENCIAS = [
  '¿Cuál fue el OTIF del mes pasado?',
  '¿Qué tenemos pendiente de facturar?',
  '¿Cuánto nos ha pagado el Éxito este mes?',
  '¿El queso de cabra tiene lactosa?',
];

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const NO_EXISTE = 'Esa conversación no existe.';

export function Patroclo(): ReactNode {
  const { id } = useParams();
  const navegar = useNavigate();
  const cliente = useQueryClient();

  const lista = useQuery({ queryKey: ['patroclo', 'conversaciones'], queryFn: conversacionesPatroclo });
  const [mensajes, setMensajes] = useState<MensajeVista[]>([]);
  const [texto, setTexto] = useState('');
  const [pensando, setPensando] = useState(false);
  const [segundosPensando, setSegundosPensando] = useState(0);
  const [abriendo, setAbriendo] = useState(false);
  const [historialAbierto, setHistorialAbierto] = useState(false);
  const [pausa, setPausa] = useState<Pausa | null>(null);
  const [ahora, setAhora] = useState(Date.now());
  /** La conversación que ya está en pantalla: recién creada no se vuelve a pedir. */
  const cargada = useRef<string | undefined>(undefined);
  const fin = useRef<HTMLDivElement>(null);
  const entrada = useRef<HTMLTextAreaElement>(null);

  const refrescarLista = (): void => void cliente.invalidateQueries({ queryKey: ['patroclo', 'conversaciones'] });

  // Abrir una conversación del historial, o empezar una nueva.
  useEffect(() => {
    setHistorialAbierto(false);
    if (id === cargada.current) return;
    cargada.current = id;
    if (!id) {
      setMensajes([]);
      return;
    }
    // Un id que no es UUID no es una conversación: se dice «no existe» sin
    // preguntar, en vez de enseñar el «uuid is expected» del proxy.
    if (!UUID.test(id)) {
      setMensajes([{ clave: 'error', rol: 'error', contenido: NO_EXISTE }]);
      return;
    }
    setAbriendo(true);
    conversacionPatroclo(id)
      .then((c) =>
        setMensajes(
          c.mensajes.map((m) => ({
            clave: m.id,
            rol: m.rol === 'usuario' ? 'usuario' : 'asistente',
            contenido: m.contenido,
            fuentes: m.fuentes,
            pasos: m.pasos,
            rechazada: m.rechazada,
            tiempoMs: m.tiempo_ms,
          })),
        ),
      )
      .catch((e: unknown) =>
        setMensajes([
          {
            clave: 'error',
            rol: 'error',
            // 404: no existe, o es de otra persona, que para quien mira es lo mismo.
            contenido:
              e instanceof ErrorApi && (e.estado === 404 || e.estado === 400)
                ? NO_EXISTE
                : e instanceof ErrorApi
                  ? e.message
                  : 'No se pudo abrir la conversación.',
          },
        ]),
      )
      .finally(() => setAbriendo(false));
  }, [id]);

  // El contador mientras Patroclo piensa: una pregunta de datos tarda de verdad.
  useEffect(() => {
    if (!pensando) return;
    setSegundosPensando(0);
    const t = setInterval(() => setSegundosPensando((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [pensando]);

  // La cuenta atrás de una pausa; al terminar, se levanta sola.
  useEffect(() => {
    if (!pausa?.hasta) return;
    const t = setInterval(() => {
      setAhora(Date.now());
      if (Date.now() >= pausa.hasta!) setPausa(null);
    }, 1000);
    return () => clearInterval(t);
  }, [pausa]);

  useEffect(() => {
    fin.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [mensajes, pensando]);

  const enviar = async (crudo: string): Promise<void> => {
    const pregunta = crudo.trim();
    if (!pregunta || pensando || pausa) return;
    setTexto('');
    setMensajes((m) => [...m, { clave: `u-${Date.now()}`, rol: 'usuario', contenido: pregunta }]);
    setPensando(true);
    try {
      const r = await preguntarAPatroclo({ pregunta, ...(id ? { conversacion_id: id } : {}) });
      setMensajes((m) => [
        ...m,
        {
          clave: `a-${Date.now()}`,
          rol: 'asistente',
          contenido: r.respuesta,
          fuentes: r.fuentes,
          pasos: r.pasos,
          rechazada: r.rechazada,
          tiempoMs: r.tiempo_ms,
          idEjecucion: r.id_ejecucion,
        },
      ]);
      if (r.conversacion_id !== id) {
        cargada.current = r.conversacion_id;
        void navegar(`/patroclo/${r.conversacion_id}`, { replace: true });
      }
      refrescarLista();
    } catch (e) {
      const p = pausaDe(e);
      if (p) {
        // Una pausa no es un error de esta pregunta: la pregunta se quita del
        // chat y vuelve a la caja, y el aviso de abajo dice cuánto esperar.
        // `ahora` se pone al día aquí: si no, la primera cuenta atrás sale con
        // el reloj de la pausa anterior y dice 44 s donde son 15.
        setAhora(Date.now());
        setPausa(p);
        setMensajes((m) => m.slice(0, -1));
      } else {
        setMensajes((m) => [
          ...m,
          { clave: `e-${Date.now()}`, rol: 'error', contenido: e instanceof ErrorApi ? e.message : 'Ocurrió un error inesperado.' },
        ]);
      }
      // Para reintentar sin volver a escribir.
      setTexto(pregunta);
    } finally {
      setPensando(false);
      entrada.current?.focus();
    }
  };

  const alEnviar = (ev: FormEvent): void => {
    ev.preventDefault();
    void enviar(texto);
  };

  // Enter envía; Shift+Enter hace un salto de línea.
  const alTeclear = (ev: KeyboardEvent<HTMLTextAreaElement>): void => {
    if (ev.key === 'Enter' && !ev.shiftKey) {
      ev.preventDefault();
      void enviar(texto);
    }
  };

  const borrar = async (c: ConversacionResumen): Promise<void> => {
    if (!window.confirm(`¿Borrar la conversación «${c.titulo}»? No se puede deshacer.`)) return;
    try {
      await borrarConversacionPatroclo(c.id);
      if (c.id === id) void navegar('/patroclo');
      refrescarLista();
    } catch (e) {
      window.alert(e instanceof ErrorApi ? e.message : 'No se pudo borrar la conversación.');
    }
  };

  return (
    <div className="space-y-3">
      <NavPatroclo />
      <div className="flex min-h-[calc(100vh-11rem)] flex-col gap-3 sm:flex-row">
        {/* El historial: en estrecho, detrás de un botón; en ancho, al lado. */}
        <aside className={`${historialAbierto ? 'block' : 'hidden'} space-y-2 sm:block sm:w-64 sm:shrink-0`}>
          <Boton onClick={() => void navegar('/patroclo')}>+ Nueva conversación</Boton>
          {lista.isError && <Error_>No se pudo cargar el historial.</Error_>}
          <ul className="space-y-1">
            {(lista.data ?? []).map((c) => (
              <li
                key={c.id}
                className={`flex items-center rounded-md border ${
                  c.id === id ? 'border-acento/40 bg-acento-tenue text-marino' : 'border-transparent hover:bg-superficie'
                }`}
              >
                <button
                  type="button"
                  onClick={() => void navegar(`/patroclo/${c.id}`)}
                  title={c.titulo}
                  className="min-h-[44px] flex-1 truncate px-2 text-left text-sm sm:min-h-0 sm:py-1.5"
                >
                  {c.titulo}
                </button>
                <button
                  type="button"
                  onClick={() => void borrar(c)}
                  aria-label={`Borrar «${c.titulo}»`}
                  title="Borrar"
                  className="flex h-11 w-11 items-center justify-center text-tenue hover:text-alerta sm:h-8 sm:w-8"
                >
                  ×
                </button>
              </li>
            ))}
            {lista.data?.length === 0 && <li className="px-2 text-xs text-tenue">Aún no tienes conversaciones.</li>}
          </ul>
        </aside>

        <section className="flex min-w-0 flex-1 flex-col">
          <div className="mb-2 sm:hidden">
            <Boton tipo="secundario" onClick={() => setHistorialAbierto((a) => !a)}>
              {historialAbierto ? 'Cerrar historial' : 'Historial'}
            </Boton>
          </div>

          <div className="flex-1 space-y-3 overflow-y-auto pb-3" aria-live="polite">
            {abriendo && <p className="p-8 text-center text-tenue">Abriendo la conversación…</p>}
            {!abriendo && mensajes.length === 0 && <Bienvenida alElegir={(s) => void enviar(s)} />}
            {mensajes.map((m) =>
              m.rol === 'asistente' ? (
                <MensajeAsistente
                  key={m.clave}
                  contenido={m.contenido}
                  fuentes={m.fuentes ?? []}
                  pasos={m.pasos ?? []}
                  rechazada={Boolean(m.rechazada)}
                  tiempoMs={m.tiempoMs ?? null}
                  idEjecucion={m.idEjecucion}
                />
              ) : m.rol === 'usuario' ? (
                <div key={m.clave} className="ml-auto w-fit max-w-[36rem] whitespace-pre-wrap rounded-2xl rounded-br-md bg-acento px-4 py-2.5 text-white shadow-sm">
                  {m.contenido}
                </div>
              ) : (
                <div key={m.clave} role="alert">
                  <Error_>{m.contenido}</Error_>
                </div>
              ),
            )}
            {pensando && (
              <div className="w-fit max-w-[46rem] rounded-2xl rounded-bl-md border border-borde bg-superficie px-4 py-3 text-tenue shadow-suave">
                <span className="mr-2 inline-flex gap-1 align-middle" aria-hidden="true">
                  <span className="punto-pensando h-1.5 w-1.5 rounded-full bg-acento" />
                  <span className="punto-pensando h-1.5 w-1.5 rounded-full bg-acento" />
                  <span className="punto-pensando h-1.5 w-1.5 rounded-full bg-acento" />
                </span>
                Patroclo está pensando… <span className="cifra">{segundosPensando} s</span>
                {segundosPensando >= 10 && (
                  <span className="block text-xs">Una pregunta sobre los datos puede tardar hasta un minuto.</span>
                )}
              </div>
            )}
            <div ref={fin} />
          </div>

          {pausa && (
            <div className="mb-2">
              <Aviso>
                {pausa.mensaje}
                {pausa.hasta && pausa.tipo === 'espera' && (
                  <> Puedes volver a preguntar en {Math.max(1, Math.ceil((pausa.hasta - ahora) / 1000))} s.</>
                )}
                {pausa.tipo === 'no_disponible' && (
                  <>
                    {' '}
                    <button type="button" className="underline" onClick={() => setPausa(null)}>
                      Volver a intentar
                    </button>
                  </>
                )}
              </Aviso>
            </div>
          )}

          {/* Abajo, donde llega el pulgar (DISENO.md). */}
          <form
            onSubmit={alEnviar}
            className="flex items-end gap-2 rounded-2xl border border-borde bg-superficie p-2 shadow-suave"
          >
            <textarea
              ref={entrada}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={alTeclear}
              placeholder="Pregunta sobre los datos o sobre los quesos…"
              rows={2}
              maxLength={1000}
              disabled={pensando || pausa !== null}
              aria-label="Tu pregunta"
              className="min-h-[44px] flex-1 resize-none rounded-xl border border-transparent bg-superficie px-3 py-2 text-base disabled:opacity-60 sm:text-sm"
            />
            <Boton submit deshabilitado={pensando || pausa !== null || !texto.trim()}>
              Enviar
            </Boton>
          </form>
        </section>
      </div>
    </div>
  );
}

function Bienvenida({ alElegir }: { alElegir: (s: string) => void }): ReactNode {
  return (
    <div className="mx-auto max-w-xl space-y-3 py-8 text-center">
      <LogoPatroclo className="mx-auto h-20 w-20 drop-shadow" />
      <h1 className="text-3xl font-semibold">¿Qué quieres saber?</h1>
      <p className="text-tenue">
        Patroclo responde con los datos de ManejoAlcanza y con la base de conocimiento de Del Pastor, y dice de dónde
        sale cada cifra. Si no lo sabe, lo dice. Solo ve lo que tu rol puede ver.
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {SUGERENCIAS.map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => alElegir(s)}
            className="min-h-[44px] rounded-xl border border-borde bg-superficie px-3 py-2.5 text-left text-sm text-marino shadow-suave transition hover:border-acento/50 hover:bg-acento-tenue"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
